<template>
  <div>
    <card id="kpiForm" :title="$t('kpi:kpiTitle')">
      <i v-if="!presetEntity" slot="header-right" class="icon-about icon-sm" @click="showInfoModal=true" />

      <template slot="card-body">
        <h3 v-if="!presetEntity">{{ $t('kpi:entity') | capitalize }}</h3>

        <div v-if="!presetEntity && isRole.superAdmin" class="form-row">
          <div class="form-group col-md-6">
            <label for="customer" class="control-label">{{ $t('kpi:customer') | capitalize }}</label>
            <select id="customer" v-model="customer" class="form-control">
              <option v-for="c in allCustomers" :key="c.id" :value="c" :data-customer-id="c.id">{{ c.name | capitalize }}</option>
            </select>
          </div>
        </div>

        <div v-if="!presetEntity && customer.id!==0" class="form-row">
          <div class="form-group col-md-6">
            <label for="fleet" class="control-label">{{ $t('kpi:fleet') | capitalize }}</label>
            <select id="fleet" v-model="fleet" class="form-control">
              <option :value="null" />
              <option v-for="f in customer.fleets.list" :key="f.id" :value="f" :data-fleet-id="f.id">{{ f.name }}</option>
            </select>
          </div>

          <div class="form-group col-md-6">
            <label for="gateway" class="control-label">{{ $t('kpi:gateway') | capitalize }}</label>
            <div class="input-group">
              <multiselect id="gateway"
                           v-model="gateway"
                           :options="gateways"
                           :multiple="false"
                           :internal-search="false"
                           :show-labels="false"
                           :options-limit="constants.maxPageSizePerFilterRequest + 1"
                           :placeholder="$t('kpi:typeAndSelectGateway') | capitalize"
                           track-by="eui"
                           label="eui"
                           @search-change="getGateways"
              >
                <span slot="noResult">{{ $t('kpi:noGateways') | capitalize }}</span>
              </multiselect>
              <span class="input-group-append">
                <button id="resetGatewayButton" class="btn btn-flat btn-sm" type="button" @click="gateway=''"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>
        </div>

        <div v-if="!presetEntity && customer.id!==0" class="form-row">
          <div class="form-group col-md-6">
            <label for="cluster" class="control-label">{{ $t('kpi:cluster') | capitalize }}</label>
            <select id="cluster" v-model="cluster" class="form-control">
              <option :value="null" />
              <option v-for="c in customer.clusters.list" :key="c.id" :value="c" :data-cluster-id="c.id">{{ c.name }}</option>
            </select>
          </div>

          <div class="form-group col-md-6">
            <label for="endDevice" class="control-label">{{ $t('kpi:endDevice') | capitalize }}</label>
            <div class="input-group">
              <multiselect id="endDevice"
                           v-model="endDevice"
                           :options="endDevices"
                           :show-labels="false"
                           :options-limit="constants.maxPageSizePerFilterRequest + 1"
                           :placeholder="$t('kpi:typeAndSelectEndDevice') | capitalize"
                           track-by="devEui"
                           label="devEui"
                           @search-change="getEndDevices"
              >
                <span slot="noResult">{{ $t('kpi:noEndDevices') | capitalize }}</span>
              </multiselect>
              <span class="input-group-append">
                <button id="resetEndDeviceButton" class="btn btn-flat btn-sm" type="button" @click="gateway=''"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>
        </div>

        <h3 v-if="!presetEntity" class="mt-2">{{ $t('kpi:kpi') | capitalize }}</h3>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="kpi" class="control-label">{{ $t('kpi:kpi') | capitalize }}</label>
            <select id="kpi" v-model="kpi" class="form-control">
              <optgroup v-for="(kpis, kpiCat) in filteredKpis" :key="kpiCat" :label="$t('kpi:kpiList.categories.' + kpiCat) | capitalize">
                <option v-for="k in kpis" :key="k.id" :value="k">{{ $t(k.name) | capitalize }}</option>
              </optgroup>
            </select>
          </div>

          <div v-if="kpi && kpi.link!=='gatewayConnections'" class="form-group col-md-6">
            <label for="period" class="control-label">{{ $t('kpi:period') | capitalize }}</label>
            <select id="period" v-model="period" class="form-control">
              <option v-if="kpi.category!=='gateway'" value="MINUTE">{{ $t('kpi:minute') | capitalize }}</option>
              <option value="HOUR">{{ $t('kpi:hour') | capitalize }}</option>
              <option value="DAY">{{ $t('kpi:day') | capitalize }}</option>
              <option v-if="kpi.category!=='gateway'" value="WEEK">{{ $t('kpi:week') | capitalize }}</option>
              <option value="MONTH">{{ $t('kpi:month') | capitalize }}</option>
              <option value="YEAR">{{ $t('kpi:year') | capitalize }}</option>
            </select>
          </div>
        </div>
      </template>

      <div slot="card-footer">
        <button id="getButton" :disabled="!canGetKpi" class="btn btn-primary" @click="getKpi">{{ $t('kpi:getKpiButton') | capitalize }}</button>
      </div>
    </card>

    <modal id="infoModal"
           v-model="showInfoModal"
           :title="$t('kpi:availableByEntity') | capitalize"
           size="lg"
    >
      <div slot="modal-body">
        <template v-for="(kpis, kpiCat) in groupedAllKpis">
          <h4 :key="kpiCat+'Title'">{{ $t('kpi:kpiList.categories.' + kpiCat) | capitalize }}</h4>
          <table :key="kpiCat+'Table'" class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>{{ $t('kpi:KPI') | capitalize }}</th>
                <th>{{ $t('kpi:customer') | capitalize }}</th>
                <!-- <th>{{ $t('kpi:fleet') | capitalize }}</th> -->
                <th>{{ $t('kpi:gateway') | capitalize }}</th>
                <!-- <th>{{ $t('kpi:cluster') | capitalize }}</th> -->
                <th>{{ $t('kpi:endDevice') | capitalize }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="k in kpis" :key="k.id">
                <td>{{ $t(k.name) | capitalize }}</td>
                <td class="text-center vertical-middle">{{ k.entities.indexOf('customer')!==-1 ? '✔' : '•' }}</td>
                <!-- <td class="text-center vertical-middle">{{ k.entities.indexOf('fleet')!==-1 ? '✔' : '•' }}</td> -->
                <td class="text-center vertical-middle">{{ k.entities.indexOf('gateway')!==-1 ? '✔' : '•' }}</td>
                <!-- <td class="text-center vertical-middle">{{ k.entities.indexOf('cluster')!==-1 ? '✔' : '•' }}</td> -->
                <td class="text-center vertical-middle">{{ k.entities.indexOf('endDevice')!==-1 ? '✔' : '•' }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </modal>
  </div>
</template>

<script>
import { filter, groupBy, isEmpty, debounce } from 'lodash-es'
import { api } from '@/api'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import constants from '@/configuration/constants'
import allKpis from '@/configuration/kpis'

const allCustomer = { id: 0 }

export default {
  props: {
    // When used in gateway / end-device page
    // Boolean to prevent disapearing effect
    presetEntity: {
      type: Boolean,
      default: false
    },
    // Gateway object
    presetGateway: {
      type: Object,
      default: () => {}
    },
    // End-device object
    presetEndDevice: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      // multiselect
      gateways: [],
      endDevices: [],
      // Selected values
      customer: allCustomer,
      fleet: null,
      gateway: null,
      cluster: null,
      endDevice: null,
      kpi: {},
      period: 'HOUR',
      // Reset boolean to prevent loop in watch
      resetFleetOrCluster: true,
      // Info modale
      showInfoModal: false
    }
  },

  computed: {
    constants () { return constants },

    allCustomers () {
      return [allCustomer, ...this.$store.getters.customers]
    },

    filteredKpis () {
      let filtered = allKpis

      if (this.gateway && !this.$store.getters.bscFeature(this.gateway, 'linkStatus')) filtered = filter(filtered, kpi => kpi.name.indexOf('linkStatus') === -1)
      if (this.gateway && !this.$store.getters.bscFeature(this.gateway, 'statistic')) filtered = filter(filtered, kpi => kpi.category.indexOf('gateway') === -1)

      if (this.gateway) filtered = filter(filtered, kpi => kpi.entities.indexOf('gateway') !== -1)
      else if (this.endDevice) filtered = filter(filtered, kpi => kpi.entities.indexOf('endDevice') !== -1)
      else if (this.fleet && !this.gateway) filtered = filter(filtered, kpi => kpi.entities.indexOf('fleet') !== -1)
      else if (this.cluster && !this.endDevice) filtered = filter(filtered, kpi => kpi.entities.indexOf('cluster') !== -1)
      else if (this.customer && !this.fleet && !this.gateway && !this.cluster && !this.endDevice) {
        if (this.customer.id === 0) filtered = filter(filtered, kpi => kpi.entities.indexOf('all') !== -1)
        else filtered = filter(filtered, kpi => kpi.entities.indexOf('customer') !== -1)
      }

      // Group by category
      return groupBy(filtered, kpi => kpi.category)
    },

    groupedAllKpis () {
      // Group by category
      return groupBy(allKpis, kpi => kpi.category)
    },

    canGetKpi () {
      if (!isEmpty(this.kpi) && this.kpi.category === 'gateway' && this.kpi.link !== 'gatewayConnections' && this.period === 'MINUTE') return false
      return (!!this.customer || !!this.fleet || !!this.gateway || !!this.cluster || !!this.endDevice) && !isEmpty(this.kpi)
    }
  },

  watch: {
    presetGateway (presetGateway) {
      this.gateway = presetGateway
    },

    presetEndDevice (presetEndDevice) {
      this.endDevice = presetEndDevice
    },

    customer (newCustomer, oldCustomer) {
      // If customer change, reset selection
      if (newCustomer !== oldCustomer) {
        this.fleet = null
        this.gateway = null
        this.cluster = null
        this.endDevice = null
        this.kpi = {}
      }

      this.getFleets()
      this.getClusters()
    },

    fleet (newFleet, oldFleet) {
      // If fleet change, reset selection
      if (newFleet !== oldFleet) {
        this.gateway = null
        this.kpi = {}

        // Prevent loop in watch
        if (this.cluster && this.resetFleetOrCluster) {
          this.cluster = null
          this.endDevice = null
          this.resetFleetOrCluster = false
        } else {
          this.resetFleetOrCluster = true
        }
      }

      // Get EUI filter list
      if (newFleet) this.getGateways()
      else this.gateways = []
    },

    cluster (newCluster, oldCluster) {
      // If fleet change, reset selection
      if (newCluster !== oldCluster) {
        this.endDevice = null
        this.kpi = {}

        // Prevent loop in watch
        if (this.fleet && this.resetFleetOrCluster) {
          this.fleet = null
          this.gateway = null
          this.resetFleetOrCluster = false
        } else {
          this.resetFleetOrCluster = true
        }
      }

      // Get EUI filter list
      if (newCluster) this.getEndDevices()
      else this.endDevices = []
    }
  },

  created () {
    if (this.presetEntity) {
      this.gateway = this.presetGateway
      this.endDevice = this.presetEndDevice
      return
    }

    // Need to translate the name here, it was not translated outside the component
    allCustomer.name = this.$t('kpi:allCustomers')

    if (this.isRole.superAdmin) {
      this.$store.dispatch('getCustomers')
    } else {
      this.customer = this.$store.getters.authenticatedUserCustomer
    }

    this.getFleets()
    this.getClusters()
  },

  methods: {
    getFleets () {
      if (this.customer.id) this.$store.dispatch('getCustomerAllFleets', this.customer)
    },

    getGateways: debounce(async function (searchQuery = '') {
      let search = []

      // Filter by selected fleet and search
      search = this.$utils.addSearch(search, 'eq', this.fleet.id, 'fleet.id')
      if (searchQuery) search = this.$utils.addSearch(search, 'like', '*' + searchQuery.toUpperCase() + '*', 'eui')
      if (search.length > 0) search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      let error, response
      [error, response] = await to(api.getGateways(
        this.$store.getters.application,
        {
          pageSize: constants.maxPageSizePerFilterRequest,
          search,
          sort: '+eui'
        }
      ))
      if (error || !response) return

      // Add ... if all results are not displayed
      if (response.data.count < response.data.totalCount) {
        response.data.list.push({ eui: '...', $isDisabled: true })
      }
      this.gateways = response.data.list
    }, 500),

    getClusters () {
      if (this.customer.id) this.$store.dispatch('getCustomerAllClusters', this.customer)
    },

    getEndDevices: debounce(async function (searchQuery = '') {
      let search = []

      // Filter by selected cluster and search
      search = this.$utils.addSearch(search, 'eq', this.cluster.id, 'cluster.id')
      if (searchQuery) search = this.$utils.addSearch(search, 'like', '*' + searchQuery.toUpperCase() + '*', 'devEui')
      if (search.length > 0) search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      let error, response
      [error, response] = await to(api.getEndDevices(
        this.$store.getters.application,
        {
          pageSize: constants.maxPageSizePerFilterRequest,
          search,
          sort: '+devEui'
        }
      ))
      if (error || !response) return

      // Add ... if all results are not displayed
      if (response.data.count < response.data.totalCount) {
        response.data.list.push({ eui: '...', $isDisabled: true })
      }
      this.endDevices = response.data.list
    }, 500),

    getKpi () {
      eventBus.$emit('kpi:getKpi', {
        customer: this.customer,
        fleet: this.fleet,
        gateway: this.gateway,
        cluster: this.cluster,
        endDevice: this.endDevice,
        kpi: this.kpi,
        period: this.period
      })
    }
  }
}
</script>
